import { c as _c } from "react/compiler-runtime";
import { type NormalizedCacheObject } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import type { EmotionCache } from '@emotion/cache';
import { AppCacheProvider } from '@mui/material-nextjs/v15-pagesRouter';
import { GoogleTagManager } from '@next/third-parties/google';
import type { AppProps as NextJsAppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { Noto_Serif } from 'next/font/google';
import localFont from 'next/font/local';
import Head from 'next/head';
import Script from 'next/script';
import { appWithTranslation } from 'next-i18next/pages';
import { DefaultSeo } from 'next-seo';
import { type ReactElement, useEffect } from 'react';
import { InstantSearchProvider } from '@core/journeys/ui/algolia/InstantSearchProvider';
import { createEmotionCache } from '@core/shared/ui/createEmotionCache';
import { FlagsProvider } from '@core/shared/ui/FlagsProvider';
import { ThemeProvider } from '@core/shared/ui/ThemeProvider';
import { ThemeMode, ThemeName } from '@core/shared/ui/themes';
import i18nConfig from '../next-i18next.config';
import { useApolloClient } from '../src/libs/apolloClient';
import 'swiper/css';
import 'swiper/css/a11y';
import 'swiper/css/navigation';
import '../public/watch/global.css';
import './fonts/fonts.css';
// eslint-disable-next-line import/no-named-as-default
const BetaBanner = dynamic(() => import(/* webpackChunkName: "beta-banner" */'../src/components/BetaBanner/index').then(mod => ({
  default: mod.BetaBanner
})), {
  ssr: false
});
const notoSerif = Noto_Serif({
  weight: ['500', '700'],
  subsets: ['latin']
});
const apercuPro = localFont({
  src: [{
    path: './fonts/Apercu-Pro-Regular.woff2',
    weight: '400',
    style: 'normal'
  }, {
    path: './fonts/Apercu-Pro-Medium.woff2',
    weight: '500',
    style: 'normal'
  }, {
    path: './fonts/Apercu-Pro-MediumItalic.woff2',
    weight: '500',
    style: 'italic'
  }, {
    path: './fonts/Apercu-Pro-Bold.woff2',
    weight: '700',
    style: 'normal'
  }, {
    path: './fonts/Apercu-Pro-BoldItalic.woff2',
    weight: '700',
    style: 'italic'
  }]
});
type WatchAppProps = NextJsAppProps<{
  flags?: {
    [key: string]: boolean;
  };
}> & {
  emotionCache?: EmotionCache;
};
function WatchApp(t0) {
  const $ = _c(23);
  const {
    Component,
    pageProps,
    emotionCache: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? createEmotionCache({}) : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const emotionCache = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  useEffect(_temp, t3);
  const initialPageProps = pageProps as {
    initialApolloState?: NormalizedCacheObject;
  };
  let t4;
  if ($[3] !== initialPageProps.initialApolloState) {
    t4 = {
      initialState: initialPageProps.initialApolloState
    };
    $[3] = initialPageProps.initialApolloState;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const client = useApolloClient(t4);
  const vercelEnv = process.env.NEXT_PUBLIC_VERCEL_ENV || "development";
  const isProduction = ["production", "prod", "prd"].includes(vercelEnv.toLowerCase());
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <style jsx={true} global={true}>{`
        :root {
          --font-noto-serif: ${notoSerif.style.fontFamily};
          --font-apercu-pro: ${apercuPro.style.fontFamily};
        }
      `}</style>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <DefaultSeo titleTemplate="%s | Jesus Film Project" defaultTitle="Watch | Jesus Film Project" description="Free Gospel Video Streaming Library. Watch, learn and share the gospel in over 2000 languages." dangerouslySetAllPagesToNoFollow={isProduction === false} dangerouslySetAllPagesToNoIndex={isProduction === false} />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Head><meta name="theme-color" content="#000" /><meta name="viewport" content="minimum-scale=1, initial-scale=1, width=device-width" /></Head>;
    t8 = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID != null && process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID !== "" && process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN != null && process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN !== "" && <Script id="datadog-rum">{`
             (function(h,o,u,n,d) {
               h=h[d]=h[d]||{q:[],onReady:function(c){h.q.push(c)}}
               d=o.createElement(u);d.async=1;d.src=n
               n=o.getElementsByTagName(u)[0];n.parentNode.insertBefore(d,n)
             })(window,document,'script','https://www.datadoghq-browser-agent.com/us1/v5/datadog-rum.js','DD_RUM')
             window.DD_RUM.onReady(function() {
               window.DD_RUM.init({
                applicationId: '${process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID ?? ""}',
                clientToken: '${process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? ""}',
                site: 'datadoghq.com',
                service: 'watch',
                env: '${process.env.NEXT_PUBLIC_VERCEL_ENV ?? ""}',
                version: '${process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? ""}',
                sampleRate: 50,
                sessionReplaySampleRate: 10,
                trackInteractions: true,
                defaultPrivacyLevel: 'mask-user-input'
               });
             })
           `}</Script>;
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <BetaBanner />;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GTM_ID ?? ""} />;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== Component || $[12] !== pageProps) {
    t11 = <ThemeProvider themeName={ThemeName.website} themeMode={ThemeMode.light}>{t9}<InstantSearchProvider>{t10}<Component {...pageProps} /></InstantSearchProvider></ThemeProvider>;
    $[11] = Component;
    $[12] = pageProps;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== emotionCache || $[15] !== t11) {
    t12 = <AppCacheProvider emotionCache={emotionCache}>{t6}{t7}{t8}{t11}</AppCacheProvider>;
    $[14] = emotionCache;
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== client || $[18] !== t12) {
    t13 = <ApolloProvider client={client}>{t12}</ApolloProvider>;
    $[17] = client;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== pageProps.flags || $[21] !== t13) {
    t14 = <>{t5}<FlagsProvider flags={pageProps.flags}>{t13}</FlagsProvider></>;
    $[20] = pageProps.flags;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  return t14;
}
function _temp() {
  const jssStyles = document.querySelector("#jss-server-side");
  if (jssStyles != null) {
    jssStyles.parentElement?.removeChild(jssStyles);
  }
}
export default appWithTranslation(WatchApp, i18nConfig);